import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MainService } from '../../services/optimizer-service/optimizer.service';
import { Trip } from '../../DataStructures/Trip/Trip';
import { CONSTANTS } from '../../utilities/Constants';
import { DateConstants } from '../../utilities/WeekSchedule/DateConstants';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { UnitConverter } from '../../utilities/unit-converter/UnitConverter';
import { AuthService } from '../../services/auth-service/auth.service';
import { environment } from '../../../environments/environment';
import { QuickDialog } from '../../utilities/Dialog/QuickDialog';
import { MatDialog } from '@angular/material';
import { SocketService } from '../../socket.service';
declare var $: any;

/** Minimal typing for ResizeObserver (not in this project's TS lib). */
interface TripsResizeObserver {
  observe(target: Element): void;
  disconnect(): void;
}

@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
  styleUrls: ['./trips.component.css'],
})
export class TripsComponent implements OnInit, AfterViewInit, OnDestroy {
  trips: Array<Trip> = [];
  id;
  @ViewChild('driverSearch') driverSearch: ElementRef;
  @ViewChild('tableScroll') tableScrollRef: ElementRef;
  @ViewChild('floatScroll') floatScrollRef: ElementRef;
  @ViewChild('floatScrollInner') floatScrollInnerRef: ElementRef;
  status = 'ALL';
  @Output() onTripChange: EventEmitter<any> = new EventEmitter();
  @Output() onTripsLoaded: EventEmitter<Trip[]> = new EventEmitter();
  @Output() onIncompleteCountChange: EventEmitter<number> = new EventEmitter();
  @Input() start: moment.Moment;
  @Input() end: moment.Moment;
  @Input() trash = false;
  @Input() incomplete = false;
  /** Overview: banner + expandable incomplete table above period table (separate APIs). */
  @Input() showIncompleteTab = false;
  /** Optional resolver from the overview map — provides per-driver hex colors. */
  @Input() tripColorResolver: { getTripMapColor(trip: any): string | null } | null = null;
  /** Second table open (incomplete trips from /trip/incomplete) */
  incompletePanelOpen = false;
  incompletePanelTrips: Trip[] = [];
  converter = new UnitConverter();
  drivers = []
  filteredDrivers = [];
  driverid = null;
  companyid = null;
  formattedNumber = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });
  adminPayload;
  editingReportDate: { [key: string]: boolean } = {};
  editingReportDateValue: { [key: string]: string } = {};
  collapsedGroups: { [status: string]: boolean } = {};
  incompletePage = 1;
  incompleteLimit = 100;
  incompleteTotalCount = 0;
  incompleteHasMore = false;
  incompleteLoading = false;
  private syncingScroll = false;
  private resizeObserver: TripsResizeObserver;
  private floatScrollCleanup: () => void;

  constructor(private main:MainService, private socketService: SocketService, private dialog: MatDialog, private serivce: MainService, private router: Router, private auth: AuthService) {
    if (!this.auth.hasOneOfRoles([CONSTANTS.ADMIN_TYPE_PROVIDER, CONSTANTS.ADMIN_TYPE_SUPER, CONSTANTS.ROLE_TYPE_DISPATCHER])) {
      this.companyid = this.auth.getClientCompanyId();
    }
  }

  ngOnInit() {
    this.adminPayload = this.auth.getAdminPayload();
    this.registerSocketListeners();
    this.serivce.drivers().then((results) => {
      this.drivers = results.data;
    }).catch(e => {

    })
    if (this.incomplete && !this.showIncompleteTab) {
      this.loadIncompleteTrips();
    }
  }

  /** Legacy: standalone incomplete-only table. Overview dual layout uses incompletePanelOpen instead. */
  get isIncompleteListActive(): boolean {
    return this.incomplete && !this.showIncompleteTab;
  }

  toggleIncompleteReview(): void {
    if (!this.showIncompleteTab) {
      return;
    }
    this.incompletePanelOpen = !this.incompletePanelOpen;
    if (this.incompletePanelOpen) {
      this.loadIncompletePanelTrips(1);
    } else {
      this.scheduleFloatingScrollbarRefresh();
    }
  }

  refreshIncompletePanel(): void {
    if (!this.showIncompleteTab || !this.incompletePanelOpen) {
      return;
    }
    this.loadIncompletePanelTrips(this.incompletePage);
  }

  private incompleteRowsForPaging(): Trip[] {
    if (this.showIncompleteTab && this.incompletePanelOpen) {
      return this.incompletePanelTrips;
    }
    if (this.isIncompleteListActive) {
      return this.trips;
    }
    return [];
  }

  /** Updates incomplete total only (does not replace the trips table rows). */
  loadIncompleteCountOnly(): Promise<any> {
    return this.serivce.getIncompleteTrips(1, 1).then((res) => {
      const pagination = res && res.pagination ? res.pagination : null;
      this.incompleteTotalCount = pagination && pagination.totalCount !== undefined ? pagination.totalCount : 0;
      this.onIncompleteCountChange.emit(this.incompleteTotalCount);
      return res;
    }).catch((e) => {
      console.log(e);
      throw e;
    });
  }

  /**
   * Reports dashboard: reload period trips, incomplete count, and (if open) the incomplete review table.
   */
  refreshOverviewData(start: moment.Moment, end: moment.Moment): Promise<any> {
    if (start) {
      this.start = start;
    }
    if (end) {
      this.end = end;
    }
    if (!this.showIncompleteTab) {
      return this.loadTrips(this.start, this.end);
    }
    const countP = this.loadIncompleteCountOnly();
    const periodP = this.loadTrips(this.start, this.end);
    if (this.incompletePanelOpen) {
      return Promise.all([periodP, countP, this.loadIncompletePanelTrips(this.incompletePage)]);
    }
    return Promise.all([periodP, countP]);
  }

  ngAfterViewInit(): void {
    this.setupFloatingScrollbar();
  }

  ngOnDestroy(): void {
    if (this.floatScrollCleanup) {
      this.floatScrollCleanup();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupFloatingScrollbar(): void {
    const tableScroll = this.tableScrollRef && this.tableScrollRef.nativeElement;
    const floatScroll = this.floatScrollRef && this.floatScrollRef.nativeElement;
    if (!tableScroll || !floatScroll) {
      return;
    }

    const syncFromTable = () => {
      if (this.syncingScroll) {
        return;
      }
      this.syncingScroll = true;
      floatScroll.scrollLeft = tableScroll.scrollLeft;
      this.syncingScroll = false;
    };

    const syncFromFloat = () => {
      if (this.syncingScroll) {
        return;
      }
      this.syncingScroll = true;
      tableScroll.scrollLeft = floatScroll.scrollLeft;
      this.syncingScroll = false;
    };

    const refresh = () => this.refreshFloatingScrollbar();

    tableScroll.addEventListener('scroll', syncFromTable);
    floatScroll.addEventListener('scroll', syncFromFloat);
    window.addEventListener('resize', refresh);

    const ResizeObserverCtor = (window as any).ResizeObserver;
    if (typeof ResizeObserverCtor === 'function') {
      this.resizeObserver = new ResizeObserverCtor(() => refresh());
      this.resizeObserver.observe(tableScroll);
      tableScroll.querySelectorAll('table').forEach((table) => {
        this.resizeObserver.observe(table);
      });
    }

    this.floatScrollCleanup = () => {
      tableScroll.removeEventListener('scroll', syncFromTable);
      floatScroll.removeEventListener('scroll', syncFromFloat);
      window.removeEventListener('resize', refresh);
    };

    this.scheduleFloatingScrollbarRefresh();
  }

  private scheduleFloatingScrollbarRefresh(): void {
    setTimeout(() => this.refreshFloatingScrollbar(), 0);
  }

  private refreshFloatingScrollbar(): void {
    const tableScroll = this.tableScrollRef && this.tableScrollRef.nativeElement;
    const floatScroll = this.floatScrollRef && this.floatScrollRef.nativeElement;
    const floatScrollInner = this.floatScrollInnerRef && this.floatScrollInnerRef.nativeElement;
    if (!tableScroll || !floatScroll || !floatScrollInner) {
      return;
    }

    const scrollWidth = tableScroll.scrollWidth;
    const clientWidth = tableScroll.clientWidth;
    floatScrollInner.style.width = `${scrollWidth}px`;
    const needsScroll = scrollWidth > clientWidth + 1;
    floatScroll.classList.toggle('trips-float-scroll--hidden', !needsScroll);
    if (needsScroll && !this.syncingScroll) {
      this.syncingScroll = true;
      floatScroll.scrollLeft = tableScroll.scrollLeft;
      this.syncingScroll = false;
    }
  }
  


  registerSocketListeners() {
    this.socketService.connect('TRIPS').then(() => {
      if (this.socketService.registerTripUpdateListener()) {
        this.socketService.onTripUpdate.subscribe((data: Trip) => {
          if (!this.shouldAcceptSocketTripUpdate(data)) {
            return;
          }
          this.applySocketTripUpdateToList(data);
          this.onTripsLoaded.emit(this.trips);
          this.onTripChange.emit();
        });
      }
    }).catch(e => {
      console.log('ERROR');
      console.log(e);
    });
  }

  private shouldAcceptSocketTripUpdate(data: any): boolean {
    if (!data) {
      return false;
    }
    if (this.showIncompleteTab) {
      if (this.companyid != null && data.companyid != null) {
        return String(data.companyid) === String(this.companyid);
      }
      return true;
    }
    if (!this.adminPayload || data.providerid == null) {
      return true;
    }
    return String(data.providerid) === String(this.adminPayload.clientid);
  }

  private applySocketTripUpdateToList(data: any): void {
    const tripId = data.id != null ? String(data.id) : (data.tripId != null ? String(data.tripId) : '');
    if (!tripId) {
      return;
    }
    const trip = this.trips.find(t => t && String((t as any).id) === tripId);
    if (!trip) {
      return;
    }
    const row = trip as any;
    if (data.type === CONSTANTS.TRIP_STARTED) {
      row.state = CONSTANTS.TRIP_STARTED;
    } else if (data.type === CONSTANTS.TRIP_COMPLETED) {
      row.state = CONSTANTS.TRIP_COMPLETED;
    } else if (data.state) {
      row.state = String(data.state).toUpperCase();
    }
  }

  editMoney(trip: Trip) {
    new QuickDialog(this.dialog).changeCost(trip).then(added => {
      this.onTripChange.emit();
    })
  }

  getJobTypeText() {
    return this.serivce.getJobTypeText();
  }


  showDriverFilter() {
    return this.auth.hasOneOfRoles([
      CONSTANTS.ADMIN_TYPE_PROVIDER,
      CONSTANTS.ADMIN_TYPE_SUPER
    ])
  }

  canModifyTrip() {
    return this.auth.hasOneOfRoles([
      CONSTANTS.ADMIN_TYPE_PROVIDER,
      CONSTANTS.ADMIN_TYPE_SUPER,
      CONSTANTS.ROLE_TYPE_DISPATCHER
    ])
  }

  getStatusClass(state) {
    if (state == CONSTANTS.TRIP_COMPLETED || state == CONSTANTS.TRIP_ENDED) {
      return "status-complete";
    } else if (state == CONSTANTS.TRIP_CREATED) {
      return "status-created";
    } else if (state == CONSTANTS.TRIP_STARTED) {
      return "status-started";
    } else if (state == CONSTANTS.TRIP_CANCELLED) {
      return "status-cancelled"
    } else {
      return 'status-created';
    }
  }

  /** Pill variant for status column (matches dashboard mockup). */
  getStatusPillModifier(state: string): string {
    const s = (state || '').toUpperCase();
    if (s === CONSTANTS.TRIP_COMPLETED || s === CONSTANTS.TRIP_ENDED) {
      return 'completed';
    }
    if (s === CONSTANTS.TRIP_STARTED) {
      return 'progress';
    }
    if (s === CONSTANTS.TRIP_CANCELLED) {
      return 'cancelled';
    }
    return 'incomplete';
  }


  viewTrip(id) {
    window.open(`${environment.SITE_URL}/admin-portal/trip-details/${id}`, '_blank');
    //this.router.navigate(['admin-portal/trip-details', id]);
  }


  getTotaCollection(trip:Trip){
    var total = 0;
    if(!trip || !trip.areas){
      return total;
    }
    trip.areas.length > 0 && trip.areas.forEach(a =>{
      if (a && a.collection) {
        total += isNaN(a.collection) ? 0 : a.collection;
      }
    })
    return total;
  }

  getDisplayedRevenue(trip: Trip) {
    if (!trip) {
      return 0;
    }

    if (this.isProvider()) {
      if (trip.earnings != null && !isNaN(Number(trip.earnings))) {
        return Number(trip.earnings);
      }
      return 0;
    }

    if (!trip.areas || trip.areas.length === 0) {
      return trip.earnings != null && !isNaN(Number(trip.earnings)) ? Number(trip.earnings) : 0;
    }

    return trip.areas.reduce((total, area: any) => {
      const fee = Number(area && area.fee);
      return total + (isNaN(fee) ? 0 : fee);
    }, 0);
  }

  getDisplayedDriverEarnings(trip: Trip) {
    const revenue = this.getDisplayedRevenue(trip);
    if (revenue <= 0) {
      return 0;
    }

    const earnings = Number(trip && trip.driverEarnings);
    return isNaN(earnings) ? 0 : earnings;
  }

  restore(trip){
    this.main.restore(trip.id).then(() =>{
      this.loadTrips();
      window.open(`${environment.SITE_URL}/admin-portal/trip-details/${trip.id}`, '_blank');
    }).catch(e =>{
      new QuickDialog(this.dialog).InfoDialog("Unable to restore the trip", "", "OK");
    })
  }

  delete(trip){
    new QuickDialog(this.dialog).DecisionDialog({
      message: `Are you sure you want to DELETE THIS TRIP FOREVER?`,
      options: [
        {
          code: 'yes',
          message: "Yes, DELETE THE TRIP",
          icon: 'fa-thumbs-up'
        },
        {
          code: 'no',
          message: 'No, cancel',
          icon: 'fa-ban'
        }
      ]
    }).then(async (choice) => {
      if (choice == 'yes') {
        this.main.deleteForever(trip.id).then(() => {
          this.loadTrips();
        }).catch(e => {
          new QuickDialog(this.dialog).InfoDialog("Unable to delete the trip", "", "OK");
        })
      }
    });
  }

  loadIncompleteTrips(page: number = 1): Promise<any> {
    this.incompleteLoading = true;
    const targetPage = Math.max(1, Number(page) || 1);
    return this.serivce.getIncompleteTrips(targetPage, this.incompleteLimit).then((res) => {
      const pageTrips = res && Array.isArray(res.data) ? res.data : [];
      const pagination = res && res.pagination ? res.pagination : null;

      if (!this.showIncompleteTab) {
        this.trips = pageTrips;
        this.onTripsLoaded.emit(this.trips);
      }
      this.incompletePage = pagination && pagination.page ? pagination.page : targetPage;
      this.incompleteTotalCount = pagination && pagination.totalCount !== undefined ? pagination.totalCount : pageTrips.length;
      this.incompleteHasMore = pagination ? !!pagination.hasMore : pageTrips.length === this.incompleteLimit;

      this.onIncompleteCountChange.emit(this.incompleteTotalCount);
      this.incompleteLoading = false;
      this.scheduleFloatingScrollbarRefresh();
      return res;
    }).catch(e => {
      this.incompleteLoading = false;
      console.log(e);
      throw e;
    });
  }

  loadIncompletePanelTrips(page: number = 1): Promise<any> {
    this.incompleteLoading = true;
    const targetPage = Math.max(1, Number(page) || 1);
    return this.serivce.getIncompleteTrips(targetPage, this.incompleteLimit).then((res) => {
      const pageTrips = res && Array.isArray(res.data) ? res.data : [];
      const pagination = res && res.pagination ? res.pagination : null;

      this.incompletePanelTrips = pageTrips;
      this.incompletePage = pagination && pagination.page ? pagination.page : targetPage;
      this.incompleteTotalCount = pagination && pagination.totalCount !== undefined ? pagination.totalCount : pageTrips.length;
      this.incompleteHasMore = pagination ? !!pagination.hasMore : pageTrips.length === this.incompleteLimit;

      this.onIncompleteCountChange.emit(this.incompleteTotalCount);
      this.incompleteLoading = false;
      this.scheduleFloatingScrollbarRefresh();
      return res;
    }).catch(e => {
      this.incompleteLoading = false;
      console.log(e);
      throw e;
    });
  }

  canGoToPreviousIncompletePage() {
    return this.incompletePage > 1 && !this.incompleteLoading;
  }

  canGoToNextIncompletePage() {
    return this.incompleteHasMore && !this.incompleteLoading;
  }

  nextIncompletePage() {
    if (!this.canGoToNextIncompletePage()) {
      return;
    }
    if (this.showIncompleteTab) {
      this.loadIncompletePanelTrips(this.incompletePage + 1);
    } else {
      this.loadIncompleteTrips(this.incompletePage + 1);
    }
  }

  previousIncompletePage() {
    if (!this.canGoToPreviousIncompletePage()) {
      return;
    }
    if (this.showIncompleteTab) {
      this.loadIncompletePanelTrips(this.incompletePage - 1);
    } else {
      this.loadIncompleteTrips(this.incompletePage - 1);
    }
  }

  getIncompletePageStart() {
    const rows = this.incompleteRowsForPaging();
    if (this.incompleteTotalCount === 0 || rows.length === 0) {
      return 0;
    }
    return ((this.incompletePage - 1) * this.incompleteLimit) + 1;
  }

  getIncompletePageEnd() {
    const rows = this.incompleteRowsForPaging();
    if (this.incompleteTotalCount === 0 || rows.length === 0) {
      return 0;
    }
    return Math.min(this.incompletePage * this.incompleteLimit, this.incompleteTotalCount);
  }

  loadTrips(start: moment.Moment = null, end: moment.Moment = null): Promise<any> {
    if (start) {
      this.start = start;
    }
    if (end) {
      this.end = end;
    }
    var status = this.status;
    if (this.status == 'ALL') {
      status = null;
    }
    if(this.trash){
      return this.serivce.getTrash(null, 0, this.start.format('YYYY-MM-DD hh:mm A'), this.end.format('YYYY-MM-DD hh:mm A'), status, this.driverid, this.companyid).then((res) => {
        this.trips = res.data;
        this.onTripsLoaded.emit(this.trips);
        this.scheduleFloatingScrollbarRefresh();
        return res;
      }).catch(e => {
        console.log(e);
        throw e;
      });
    }else{
      return this.serivce.getAllTrips(null, 0, this.start.format('YYYY-MM-DD hh:mm A'), this.end.format('YYYY-MM-DD hh:mm A'), status, this.driverid, this.companyid).then((res) => {
        this.trips = res.data;
        this.onTripsLoaded.emit(this.trips);
        this.scheduleFloatingScrollbarRefresh();
        return res;
      }).catch(e => {
        console.log(e);
        throw e;
      });
    }
  }
  getPickupType(trip) {
    return Trip.getPickupType(trip).toUpperCase();
  }
  getSourceLabel(trip) {
    if (!trip) {
      return "...";
    }
    return Trip.getSourceLabel(trip);
  }

  getDestinationLabel(trip) {
    if (!trip) {
      return "...";
    }
    return Trip.getDestinationLabel(trip);
  }
  setDriverId(driverid) {
    this.driverid = driverid;
    this.loadTrips();
  }

  searchTrip() {
    var status = this.status;
    if (this.status == 'ALL') {
      status = null;
    }
    this.serivce.getAllTrips(this.id.toUpperCase(), 0, this.start.format('YYYY-MM-DD'), this.end.format('YYYY-MM-DD'), status, this.driverid, this.companyid).then((res) => {
      this.trips = res.data;
    }).catch(e => {
      console.log(e);
    });
  }

  onDriverSearch(event) {
    if (!this.driverSearch.nativeElement.value) {
      this.filteredDrivers = this.drivers;
    } else {
      this.filteredDrivers = this.drivers.filter(x =>
        (`${x.firstname} ${x.lastname}`.toLocaleLowerCase()).includes(this.driverSearch.nativeElement.value.toLocaleLowerCase())
      );
    }
  }

  setSelectedCompany(company) {
    this.companyid = company.companyid;
    this.loadTrips();
  }

  formatReportDate(date: string) {
    if (date) {
      return moment(date).format(DateConstants.shortDateTimeFormat2);
    } else {
      return 'N/A';
    }
  }

  refresh() {
    if (this.isIncompleteListActive) {
      this.loadIncompleteTrips(this.incompletePage);
      return;
    }
    this.loadTrips();
    if (this.showIncompleteTab) {
      this.loadIncompleteCountOnly();
      if (this.incompletePanelOpen) {
        this.loadIncompletePanelTrips(this.incompletePage);
      }
    }
  }

  formatDate(date) {
    if (date) {
      return moment(date).format(DateConstants.shortDateTimeFormat2);
    } else {
      return 'N/A';
    }
  }
  isBearer() {
    var payload = this.auth.getAdminPayload();
    if (payload.type == 'BEARER') {
      return true;
    }
    return false;
  }

  isProvider() {
    const payload = this.auth.getAdminPayload();
    return !!(payload && payload.type === 'PROVIDER');
  }

  hideCost() {
    return this.auth.hasOneOfRoles([
      CONSTANTS.ROLE_TYPE_DISPATCHER
    ]);
  }

  startEditReportDate(trip: Trip) {
    this.editingReportDate[trip.id] = true;
    // Initialize the daterangepicker after the view is updated
    setTimeout(() => {
      var startDate = trip.reportDate ? moment(trip.reportDate) : moment();
      $(`#reportDatePicker_${trip.id}`).daterangepicker({
        singleDatePicker: true,
        startDate: startDate,
        timePicker: true,
        drops: 'up',
        locale: {
          format: 'DD, MMM YYYY hh:mm A'
        },
      }, (sD: moment.Moment, eD: moment.Moment, label) => {
        this.editingReportDateValue[trip.id] = sD.format('YYYY-MM-DD hh:mm A');
      });
    }, 100);
  }

  saveReportDate(trip: Trip) {
    if (!this.editingReportDateValue[trip.id]) {
      new QuickDialog(this.dialog).InfoDialog("Please select a valid date", "Invalid Date", "OK");
      return;
    }
    
    trip.reportDate = moment(this.editingReportDateValue[trip.id], 'YYYY-MM-DD hh:mm A').toDate();
    this.updateReportDate(trip);
    this.editingReportDate[trip.id] = false;
    this.editingReportDateValue[trip.id] = '';
  }

  cancelEditReportDate(trip: Trip) {
    this.editingReportDate[trip.id] = false;
    this.editingReportDateValue[trip.id] = '';
    // Destroy the daterangepicker instance
    if ($(`#reportDatePicker_${trip.id}`).data('daterangepicker')) {
      $(`#reportDatePicker_${trip.id}`).data('daterangepicker').remove();
    }
  }

  updateReportDate(trip: Trip) {
    if (!trip.reportDate) {
      new QuickDialog(this.dialog).InfoDialog("Please select a valid date", "Invalid Date", "OK");
      return;
    }
    
    this.main.updateReportDate(trip.id, trip.reportDate.toISOString()).then(() => {
      new QuickDialog(this.dialog).InfoDialog("Trip date updated successfully", "Success", "OK");
      this.refresh();
    }).catch(e => {
      new QuickDialog(this.dialog).InfoDialog("Unable to update trip date, please try again later", "Update Failed", "OK");
    });
  }


  getRating(trip: Trip) {
    if (!trip || !trip.rating) {
      return "...";
    }
    
    const ratingObj = trip.rating;
    const rating = ratingObj.rating || 0;
    const driver = ratingObj.driver || 0;
    const recommend = ratingObj.recommend || 0;
    
    const average = (rating + driver + recommend) / 3;
    return Math.round(average * 10) / 10; // Round to 1 decimal place
  }

  viewRating(trip: Trip) {
    if (!trip || !trip.rating) {
      return;
    }

    // Use the DeliveryRatingDialog method with read-only mode
    new QuickDialog(this.dialog).DeliveryRatingDialog(trip, true).then(() => {
      // Dialog closed
    }).catch(e => {
      console.error('Error opening rating dialog:', e);
    });
  }

  private readonly STATUS_GROUP_ORDER = ['CREATED', 'STARTED', 'COMPLETED', 'ENDED', 'CANCELLED'];

  get tripsGroupedByStatus(): Array<{ status: string; trips: Trip[] }> {
    const grouped: { [key: string]: Trip[] } = {};
    this.trips.forEach(trip => {
      const s = (trip.state || 'UNKNOWN').toUpperCase();
      if (!grouped[s]) { grouped[s] = []; }
      grouped[s].push(trip);
    });

    const result: Array<{ status: string; trips: Trip[] }> = [];
    this.STATUS_GROUP_ORDER.forEach(s => {
      if (grouped[s] && grouped[s].length > 0) {
        result.push({ status: s, trips: grouped[s] });
      }
    });
    Object.keys(grouped).forEach(s => {
      if (!this.STATUS_GROUP_ORDER.includes(s) && grouped[s].length > 0) {
        result.push({ status: s, trips: grouped[s] });
      }
    });
    return result;
  }

  toggleStatusGroup(status: string): void {
    this.collapsedGroups[status] = !this.collapsedGroups[status];
    this.scheduleFloatingScrollbarRefresh();
  }

  isGroupCollapsed(status: string): boolean {
    return !!this.collapsedGroups[status];
  }

  /** True when a platform driver (`driverid`) or portal driver (`driver_kind` + `portal_driver_id`) is assigned. */
  tripHasAssignedDriver(trip: any): boolean {
    if (!trip || !trip.vehicleReservation || !trip.vehicleReservation.vehicle) {
      return false;
    }
    const v = trip.vehicleReservation.vehicle;
    const rawId = v.driverid;
    if (rawId != null) {
      const n = Number(rawId);
      if (!isNaN(n) && n > 0) {
        return true;
      }
    }
    if (String(v.driver_kind || '').toLowerCase() !== 'portal') {
      return false;
    }
    const pid = v.portal_driver_id;
    if (pid == null) {
      return false;
    }
    const pn = Number(pid);
    return !isNaN(pn) && pn > 0;
  }

  /** Returns the two-letter initials for the driver assigned to a trip. */
  getDriverInitials(trip: any): string {
    if (!trip || !trip.vehicleReservation || !trip.vehicleReservation.vehicle) {
      return '?';
    }
    const v = trip.vehicleReservation.vehicle;
    const first = String(v.driverfirstname || '').trim();
    const last = String(v.driverlastname || '').trim();
    const f = first ? first[0].toUpperCase() : '';
    const l = last ? last[0].toUpperCase() : '';
    return (f + l) || (first.substring(0, 2).toUpperCase()) || '?';
  }

  /** Returns a slightly-tinted background derived from the accent color. */
  getDriverInitialsBg(color: string): string {
    if (!color || color.length < 4) {
      return color;
    }
    try {
      const hex = color.replace('#', '');
      const full = hex.length === 3
        ? hex.split('').map(c => c + c).join('')
        : hex;
      const r = parseInt(full.substring(0, 2), 16);
      const g = parseInt(full.substring(2, 4), 16);
      const b = parseInt(full.substring(4, 6), 16);
      const tr = Math.round(r + (255 - r) * 0.82);
      const tg = Math.round(g + (255 - g) * 0.82);
      const tb = Math.round(b + (255 - b) * 0.82);
      return `#${tr.toString(16).padStart(2, '0')}${tg.toString(16).padStart(2, '0')}${tb.toString(16).padStart(2, '0')}`;
    } catch (_) {
      return color;
    }
  }

  /** Returns the map hex color for the driver assigned to this trip, or null. */
  getTripDriverColor(trip: any): string | null {
    if (!this.tripColorResolver) {
      return null;
    }
    return this.tripColorResolver.getTripMapColor(trip);
  }

}
